import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { cmsPublic } from '../../lib/cmsApi';
import SiteShell from '../../components/site/SiteShell';
import { getBlockComponent } from '../../components/site/blocks';

/**
 * PublicSitePage — resolves the slug from /site/* and renders ordered blocks
 * pulled from the CMS. Falls back to a 404 if the page is missing or unpublished.
 */
export default function PublicSitePage() {
  const params = useParams();
  // useParams '*' for splat routes
  const slug = (params['*'] || params.slug || 'home').replace(/^\/+|\/+$/g, '') || 'home';
  const [state, setState] = useState({ loading: true, page: null, blocks: [], error: null });

  useEffect(() => {
    let cancelled = false;
    setState({ loading: true, page: null, blocks: [], error: null });
    cmsPublic.getPage(slug)
      .then(d => {
        if (cancelled) return;
        setState({ loading: false, page: d.page, blocks: d.blocks || [], error: null });
        if (d?.page?.seo_title) document.title = d.page.seo_title;
        else if (d?.page?.title) document.title = d.page.title;
      })
      .catch(err => {
        if (cancelled) return;
        setState({ loading: false, page: null, blocks: [], error: err });
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (state.loading) {
    return (
      <SiteShell>
        <div className="max-w-6xl mx-auto px-6 py-24" data-testid="site-page-loading">
          <div className="h-8 w-48 rounded-md orka-surface-subtle animate-pulse" />
          <div className="mt-4 h-12 w-3/4 rounded-md orka-surface-subtle animate-pulse" />
          <div className="mt-3 h-6 w-1/2 rounded-md orka-surface-subtle animate-pulse" />
        </div>
      </SiteShell>
    );
  }

  if (state.error || !state.page) {
    // Send "/site" / "/site/" to home; 404 for unknown slugs
    if (!slug || slug === '' || slug === 'site') return <Navigate to="/site/home" replace />;
    return (
      <SiteShell>
        <div className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="site-page-404">
          <div className="text-xs uppercase tracking-wider orka-text-tertiary font-semibold">404</div>
          <h1 className="mt-3 text-3xl font-bold orka-text-primary">Page not found</h1>
          <p className="mt-2 orka-text-secondary">
            We couldn&rsquo;t find <code className="font-mono text-sm">/site/{slug}</code>. It might be unpublished or moved.
          </p>
        </div>
      </SiteShell>
    );
  }

  return (
    <SiteShell>
      <article data-testid={`site-page-${state.page.page_slug}`}>
        {state.blocks.map((b, i) => {
          const Cmp = getBlockComponent(b.block_type);
          if (!Cmp) {
            return (
              <div key={b.block_id || i} className="max-w-6xl mx-auto px-6 py-2 text-xs orka-text-tertiary">
                Unsupported block type: {b.block_type}
              </div>
            );
          }
          return <Cmp key={b.block_id || i} data={b.data || {}} />;
        })}
      </article>
    </SiteShell>
  );
}
